const absoluteUrlPattern = /^[a-zA-Z][a-zA-Z\d+.-]*:/;
const emailPattern = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const longTokenPattern = /\b[A-Za-z0-9_-]{32,}\b/g;
const sensitiveAssignmentPattern =
  /\b(authorization|cookie|set-cookie|password|passwd|token|secret|api[-_ ]?key)\s*[:=]\s*([^\s,;]+)/gi;
const uuidPattern =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;

const normalizeIncidentText = (value: unknown, maximum: number): string => {
  if (typeof value !== "string") return "";
  const normalized = value.replace(/\r\n/g, "\n").trim();
  if (!normalized) return "";
  return normalized.length > maximum
    ? normalized.slice(0, maximum).trimEnd()
    : normalized;
};

const maskDynamicPathSegments = (pathname: string): string => {
  const segments = pathname.split("/");
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index] ?? "";
    const previous = segments[index - 1] ?? "";
    if (previous === "sessions" && index + 1 < segments.length) {
      segments[index + 1] = ":session";
      index += 1;
      continue;
    }
    if (
      /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment) ||
      /^019[0-9a-z]{20,}$/i.test(segment) ||
      /^[0-9a-f]{16,}$/i.test(segment) ||
      /^\d{5,}$/.test(segment)
    ) {
      segments[index] = ":id";
    }
  }
  return segments.join("/") || "/";
};

export const sanitizeFlowTraceIncidentRoute = (
  value: unknown,
  maximum = 240,
): string => {
  const raw = normalizeIncidentText(value, maximum * 2);
  if (!raw) return "/";
  try {
    const parsed = new URL(raw, "https://flowtrace.invalid");
    return normalizeIncidentText(
      maskDynamicPathSegments(parsed.pathname || "/"),
      maximum,
    );
  } catch {
    return normalizeIncidentText(
      maskDynamicPathSegments(raw.split(/[?#]/, 1)[0] || "/"),
      maximum,
    );
  }
};

export const sanitizeFlowTraceIncidentHref = (
  value: unknown,
  baseUrl?: string,
  maximum = 1_000,
): string => {
  const raw = normalizeIncidentText(value, maximum * 2);
  if (!raw) return "";
  try {
    const parsed = new URL(raw, baseUrl || "https://flowtrace.invalid");
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    const pathname = maskDynamicPathSegments(parsed.pathname || "/");
    if (!absoluteUrlPattern.test(raw)) {
      return normalizeIncidentText(pathname, maximum);
    }
    return normalizeIncidentText(`${parsed.origin}${pathname}`, maximum);
  } catch {
    return sanitizeFlowTraceIncidentRoute(raw, maximum);
  }
};

export const sanitizeFlowTraceIncidentText = (
  value: unknown,
  maximum = 4_000,
): string => {
  const raw = normalizeIncidentText(value, maximum * 2);
  if (!raw) return "";
  const redacted = raw
    .replace(/https?:\/\/[^\s)"']+/gi, (match) =>
      sanitizeFlowTraceIncidentHref(match),
    )
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [redacted]")
    .replace(sensitiveAssignmentPattern, "$1=[redacted]")
    .replace(/(^|\n)\s*params?:[^\n]*/gi, "$1params: [redacted]")
    .replace(emailPattern, "[redacted-email]")
    .replace(uuidPattern, ":id")
    .replace(longTokenPattern, "[redacted-token]");
  return normalizeIncidentText(redacted, maximum);
};

export const normalizeFlowTraceIncidentMessage = (
  value: unknown,
  maximum = 320,
): string => {
  const text = sanitizeFlowTraceIncidentText(value, maximum * 2)
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return text.length > maximum
    ? `${text.slice(0, Math.max(0, maximum - 1)).trimEnd()}…`
    : text;
};

export const stableFlowTraceIncidentHash = (value: string): string => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};
